import { useCallback, useEffect, useRef } from 'react';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { wrapThunkAction } from 'redux/utils';

import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import {
  resetAiSuggestComponentsState,
  resetDestinationLecturePage,
  setAddedSuggestedComponentSetIds,
  setNovoAIShowGenerateModal,
  setNovoAIShowPreviewModal,
} from 'redux/actions/lecture-pages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { LectureComponent } from 'redux/schemas/models/lecture-component';
import { RootState } from 'redux/schemas';

import LecturePagePreviewModal, { LecturePagePreviewContext, PreviewSource } from 'lecture_pages/components/lecture-page-preview-modal';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import { addNewComponent } from 'redux/actions/lecture-components';
import { getNovoAIProperties, getSuggestedActivities } from 'redux/selectors/lecture-page';
import { getCourseAliases } from 'redux/selectors/course';

import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import useAIComponentsGenerator, {
  AIComponentsContextProvider,
} from '../hooks/use-ai-components-generator';
import GenerateDiscussionPrompts from './discussion-prompts/generate-prompts';
import LayoutList from './key-takeaway-and-summary/select-layout';
import GenerateQuiz from './quiz/generate-quiz';
import AiGeneratingModal from './shared/ai-generating-modal';
import useAiSuggestComponents from './suggest-activities/hooks/use-ai-suggest-components';
import GenerateActivityButton from './shared/generate-activity-button';

const contentRenderMap = {
  [NovoAIItemType.SUMMARY]: LayoutList,
  [NovoAIItemType.KEY_TAKEAWAYS]: LayoutList,
  [NovoAIItemType.DISCUSSION]: GenerateDiscussionPrompts,
  [NovoAIItemType.QUIZ]: GenerateQuiz,
  [NovoAIItemType.POLL]: GenerateActivityButton,
  [NovoAIItemType.PUBLIC_FEEDBACK]: GenerateActivityButton,
  [NovoAIItemType.ASSIGNMENT]: GenerateActivityButton,
  [NovoAIItemType.PRE_SURVEY]: GenerateActivityButton,
  [NovoAIItemType.POST_SURVEY]: GenerateActivityButton,
};

type AIContentRenderProps = {
  itemType: NovoAIItemType;
};

const ContentRender = (props: AIContentRenderProps) => {
  const RenderComponent = contentRenderMap[props.itemType];
  const useAIComponentsMethods = useAIComponentsGenerator();
  const contentRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const novoAIProperties = useSelector(getNovoAIProperties);
  const activities = useSelector(getSuggestedActivities);
  const aliases = useSelector((state) => getCourseAliases(state));
  const { activitySets, addingComponentSetId } = useSelector((state: RootState) => state.app.aiSuggestComponent);

  const params = useLecturePageParams();
  const { buildComponentCreationPayload, componentTextMapping } = useAiSuggestComponents();

  const { itemType, showGenerateModal, showPreviewModal, content } = novoAIProperties;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (content?.id && contentRef.current) {
      timeoutId = setTimeout(() => {
        contentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }

    return () => clearTimeout(timeoutId);
  }, [content?.id]);

  const onClosePreviewModal = useCallback(() => {
    dispatch(setNovoAIShowPreviewModal(false));
    hideAddUI(dispatch);
    dispatch(resetDestinationLecturePage());
    dispatch(resetAiSuggestComponentsState());
  }, [dispatch]);

  const showSuccessAlert = useCallback((lc: LectureComponent) => {
    let header = t.FORM.SUCCESS_BANG();
    let alertMessage = t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.ADDED_ALERT({
      type: componentTextMapping[lc.type],
      lectureAlias: aliases.lectureAliases.lectureAlias,
    });

    if (lc.aiOrigin === NovoAIItemType.SUMMARY || lc.aiOrigin === NovoAIItemType.KEY_TAKEAWAYS) {
      alertMessage = t.LECTURE_PAGES.LHS.NOVO_AI.ALERT.SUCCESS.CONTENT();
      header = t.LECTURE_PAGES.LHS.NOVO_AI.ALERT.SUCCESS.HEADER();
    }

    dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header,
      message: alertMessage,
    }));
  }, [componentTextMapping, aliases.lectureAliases.lectureAlias, dispatch]);

  const onAddLesson = useCallback((index: number) => {
    const component = activitySets[addingComponentSetId]?.activity;
    if (!component) {
      return;
    }

    const payload = buildComponentCreationPayload(component, index, itemType);

    wrapThunkAction(dispatch(addNewComponent(payload)))
      .then((response) => {
        const lc = response.payload as LectureComponent;
        showSuccessAlert(lc);
      })
      .catch(() => {
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.OOPS(),
          message: t.FORM.ERROR_SOMETHING_WRONG(),
        }));
      })
      .finally(() => {
        // Showing list if there are multiple feedback activities to choose from
        if (itemType === NovoAIItemType.PUBLIC_FEEDBACK && activities.length > 1) {
          dispatch(setNovoAIShowPreviewModal(false));
          dispatch(setNovoAIShowGenerateModal(true));
          dispatch(setAddedSuggestedComponentSetIds(addingComponentSetId));
        } else {
          dispatch(resetAiSuggestComponentsState());
          onClosePreviewModal();
        }
      });
  }, [
    activities.length, activitySets, addingComponentSetId,
    buildComponentCreationPayload, dispatch, itemType,
    onClosePreviewModal, showSuccessAlert,
  ]);

  const openCurrentLessonPreview = useCallback(() => {
    dispatch(setNovoAIShowGenerateModal(false));
    dispatch(setNovoAIShowPreviewModal(true));
  }, [dispatch]);

  if (!RenderComponent) {
    return null;
  }

  return (
    <AIComponentsContextProvider
      {...useAIComponentsMethods}
      openCurrentLessonPreview={openCurrentLessonPreview}
    >
      <div ref={contentRef}>
        <RenderComponent />
        <AiGeneratingModal showModal={showGenerateModal} />
        <LecturePagePreviewContext.Provider value={{
          lecturePageId: params.lecturePageId,
          source: PreviewSource.AI_ADD,
          onClosePreviewModal,
          onAction: onAddLesson,
        }}
        >
          <LecturePagePreviewModal
            show={showPreviewModal}
            onClose={onClosePreviewModal}
            header={t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.PREVIEW_MODAL_HEADER()}
          />
        </LecturePagePreviewContext.Provider>
      </div>
    </AIComponentsContextProvider>
  );
};

export default ContentRender;

